import { MainContent } from '../../shared-mui-components/MainContent.tsx';
import { Localized } from 'dg-web-shared/lib/Localization.tsx';
import { OperatorAsyncLoadedSection } from '../../app/components/OperatorAsyncLoadedSection.tsx';
import { LoadingSpinnerPresets } from 'dg-web-shared/common/components/material-ui/PresetLoadingSpinner.tsx';
import { EmptyResults } from '../../shared-mui-components/ListMessages.tsx';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
} from '@mui/material';
import { Download, PictureAsPdf, PointOfSale } from '@mui/icons-material';
import { useServerFetch } from 'dg-web-shared/lib/hooks/ServerStateHooks.ts';
import { MaterialHeader } from '../../layout/components/MaterialHeader.tsx';
import { MaterialTitle } from '../../layout/components/MaterialTitle.tsx';
import { SeparationBar } from '../../layout/components/SeparationBar.tsx';
import { currencyCentsToLocalPrice } from 'dg-web-shared/lib/NumberFormatter.ts';
import { css } from '@emotion/css';
import { EmDash } from 'dg-web-shared/lib/Punctuation.ts';
import { Link } from 'react-router-dom';
import { useOperatorContext } from '../../app/components/BaseLayoutAndData.tsx';
import { urlWithOperatorExternalId } from '../../utils/Utils.ts';

interface PayStationStatementResponse {
    monthlyStatements: PayStationStatementItem[];
    summary: PayStationStatementsSummary;
}

interface PayStationStatementsSummary {
    cashlessPaymentsRappen: number;
    cashPaymentsRappen: number;
    totalRappen: number;
}

interface PayStationStatementItem extends PayStationStatementsSummary {
    isCurrentMonth: boolean;
    hasCashlessReport: boolean;
    period: string;
    monthlyCollection: PayStationMonthlyCollection | null;
}

interface PayStationMonthlyCollection {
    collectionCount: number;
    totalAmountRappen: number;
}

export function PayStationStatements() {
    const [payStationStatementResultState] = useServerFetch<
        PayStationStatementResponse,
        object
    >(
        () => ({
            url: `/ui-api/operator-account/pay-station/monthly-statement`,
        }),
        {},
    );

    return (
        <MainContent>
            <MaterialHeader editMode={false}>
                <MaterialTitle
                    Icon={PointOfSale}
                    title={
                        <Localized
                            de="Kassenauszug"
                            fr="Relevé de caisse"
                            it="Estratto cassa"
                            en="Pay station statement"
                        />
                    }
                />
            </MaterialHeader>
            <SeparationBar />
            <OperatorAsyncLoadedSection
                requestState={payStationStatementResultState}
                pendingLoaderPreset={
                    LoadingSpinnerPresets.FillAllSpaceAndCenter
                }
                render={data => {
                    if (data.monthlyStatements.length === 0) {
                        return <EmptyResults hasFilter={false} />;
                    }
                    return (
                        <TableContainer>
                            <Table stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell>
                                            <Localized
                                                de="Monat"
                                                fr="Mois"
                                                it="Mese"
                                                en="Month"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Box
                                                sx={{
                                                    display: 'flex',
                                                    justifyContent: 'flex-end',
                                                }}
                                            >
                                                <Localized
                                                    de="Bargeldlose Zahlungen (CHF)"
                                                    fr="Paiements sans espèces (CHF)"
                                                    it="Pagamenti senza contanti (CHF)"
                                                    en="Cashless payments (CHF)"
                                                />
                                                <DownloadSpacer />
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Localized
                                                de="Barzahlungen (CHF)"
                                                fr="Paiements en espèces (CHF)"
                                                it="Pagamenti in contanti (CHF)"
                                                en="Cash payments (CHF)"
                                            />
                                        </TableCell>
                                        <TableCell align="right">
                                            <Localized
                                                de="Gesamt (CHF)"
                                                fr="Totale (CHF)"
                                                it="Totale (CHF)"
                                                en="Total (CHF)"
                                            />
                                        </TableCell>
                                        <TableCell>PDF</TableCell>
                                        <TableCell align="right">
                                            <Localized
                                                de="Leerungen (Anzahl/CHF)"
                                                fr="Collectes (Quantité/CHF)"
                                                it="Vuotature (Quantità/CHF)"
                                                en="Collections (Count/CHF)"
                                            />
                                        </TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <PayStationSummaryRow
                                        summary={data.summary}
                                    />
                                    {data.monthlyStatements.map(
                                        monthlyStatement => (
                                            <PayStationStatementRow
                                                key={monthlyStatement.period}
                                                monthlyStatement={
                                                    monthlyStatement
                                                }
                                            />
                                        ),
                                    )}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    );
                }}
            />
        </MainContent>
    );
}

function PayStationSummaryRow({
    summary,
}: {
    summary: PayStationStatementsSummary;
}) {
    return (
        <TableRow
            sx={{
                color: theme => theme.palette.primary.contrastText,
                backgroundColor: theme => theme.palette.primary.main,
            }}
        >
            <TableCell sx={{ color: 'inherit' }}>
                <Localized de="Gesamt" fr="Totale" it="Totale" en="Total" />
            </TableCell>
            <TableCell sx={{ color: 'inherit' }} align="right">
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                    }}
                >
                    {currencyCentsToLocalPrice(
                        summary.cashlessPaymentsRappen,
                        false,
                    )}
                    <DownloadSpacer />
                </Box>
            </TableCell>
            <AmountChfField amountRappen={summary.cashPaymentsRappen} />
            <AmountChfField amountRappen={summary.totalRappen} />
            <TableCell />
            <TableCell />
        </TableRow>
    );
}

function PayStationStatementRow({
    monthlyStatement,
}: {
    monthlyStatement: PayStationStatementItem;
}) {
    const { operatorExternalId } = useOperatorContext();
    const [year, month] = monthlyStatement.period.split('-');
    return (
        <TableRow sx={{ color: theme => theme.palette.primary.main }}>
            <TableCell>{monthlyStatement.period}</TableCell>

            <TableCell align="right">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    {currencyCentsToLocalPrice(
                        monthlyStatement.cashlessPaymentsRappen,
                        false,
                    )}
                    {monthlyStatement.hasCashlessReport ? (
                        <a
                            target="_blank"
                            href={urlWithOperatorExternalId(
                                `/ui-api/operator-account/cashless/reconciliation/${year}/${month}`,
                                operatorExternalId,
                            )}
                            rel="noreferrer"
                            className={css({
                                lineHeight: 0,
                            })}
                        >
                            <Download
                                sx={theme => ({
                                    paddingLeft: '4px',
                                    color: theme.palette.primary.main,
                                    '&:hover': {
                                        color: theme.palette.blue.electric,
                                    },
                                })}
                            />
                        </a>
                    ) : (
                        <DownloadSpacer />
                    )}
                </Box>
            </TableCell>
            <AmountChfField
                amountRappen={monthlyStatement.cashPaymentsRappen}
            />
            <AmountChfField amountRappen={monthlyStatement.totalRappen} />
            <TableCell>
                {!monthlyStatement.isCurrentMonth && (
                    <a
                        target="_blank"
                        href={urlWithOperatorExternalId(
                            `/ui-api/operator-account/pay-station/monthly-statement/pdf/${year}/${month}`,
                            operatorExternalId,
                        )}
                        rel="noreferrer"
                    >
                        <PictureAsPdf
                            sx={{
                                color: theme => theme.palette.primary.main,
                                '&:hover': {
                                    color: theme => theme.palette.blue.electric,
                                },
                            }}
                        />
                    </a>
                )}
            </TableCell>
            <TableCell align="right">
                {monthlyStatement.monthlyCollection ? (
                    <Box
                        sx={{
                            display: 'flex',
                            flexShrink: 1,
                            justifyContent: 'flex-end',
                        }}
                    >
                        <Link
                            to={`../pay-station-collections/${year}/${month}`}
                        >
                            <Box
                                sx={{
                                    color: theme => theme.palette.blue.main,
                                    textDecoration: 'underline',
                                }}
                            >
                                {`${
                                    monthlyStatement.monthlyCollection
                                        .collectionCount
                                } / ${currencyCentsToLocalPrice(
                                    monthlyStatement.monthlyCollection
                                        .totalAmountRappen,
                                    false,
                                )}`}
                            </Box>
                        </Link>
                    </Box>
                ) : (
                    EmDash
                )}
            </TableCell>
        </TableRow>
    );
}

function AmountChfField({ amountRappen }: { amountRappen: number }) {
    return (
        <TableCell sx={{ color: 'inherit' }} align="right">
            {currencyCentsToLocalPrice(amountRappen, false)}
        </TableCell>
    );
}

function DownloadSpacer() {
    return <Box sx={{ paddingX: '12px' }} />;
}
